// In-house replacement for react-notifications' NotificationManager.
// Same call-site API (info/success/warning/error(message, title, timeOut, onClick, priority)),
// plus: a cap on simultaneously visible toasts, a 5s ceiling on auto-dismiss,
// and a history of the latest messages consumed by the NavMenu inbox.

const MAX_VISIBLE = 3;
const MAX_HISTORY = 30;
const DEFAULT_TIMEOUT = 5000;
const MAX_TIMEOUT = 5000;

let idCounter = 0;
const createId = () => `notification-${++idCounter}`;

class NotificationStore {
    constructor() {
        this.visible = [];
        this.history = [];
        this.changeListeners = [];
        this.historyListeners = [];
    }

    create = (notification) => {
        // timeOut === 0 means persistent (e.g. session-expired warning); anything else is clamped to MAX_TIMEOUT
        const timeOut = notification.timeOut === 0 ? 0 : Math.min(notification.timeOut || DEFAULT_TIMEOUT, MAX_TIMEOUT);
        const item = { ...notification, id: createId(), time: new Date(), seen: false, timeOut };

        if (item.priority) {
            this.visible.unshift(item);
        }
        else {
            this.visible.push(item);
        }

        // Cap the stack so toasts never take over the screen; evict the oldest,
        // preferring non-persistent ones and never the item just added (a priority
        // item sits at position 0 and must not evict itself).
        while (this.visible.length > MAX_VISIBLE) {
            const evictable = this.visible.find(n => n.id !== item.id && n.timeOut !== 0)
                || this.visible.find(n => n.id !== item.id)
                || this.visible[0];
            this.visible = this.visible.filter(n => n.id !== evictable.id);
        }

        this.history.unshift(item);
        if (this.history.length > MAX_HISTORY) {
            this.history = this.history.slice(0, MAX_HISTORY);
        }

        this.emitChange();
        this.emitHistoryChange();
    }

    info = (message, title, timeOut, onClick, priority) => {
        this.create({ type: 'info', message, title, timeOut, onClick, priority });
    }

    success = (message, title, timeOut, onClick, priority) => {
        this.create({ type: 'success', message, title, timeOut, onClick, priority });
    }

    warning = (message, title, timeOut, onClick, priority) => {
        this.create({ type: 'warning', message, title, timeOut, onClick, priority });
    }

    error = (message, title, timeOut, onClick, priority) => {
        this.create({ type: 'error', message, title, timeOut, onClick, priority });
    }

    remove = (notification) => {
        const removed = this.visible.find(n => n.id === notification.id);
        this.visible = this.visible.filter(n => n.id !== notification.id);
        this.emitChange();
        if (removed && removed.onRemove) {
            removed.onRemove();
        }
    }

    getVisible = () => this.visible;

    getHistory = () => this.history;

    getUnseenCount = () => this.history.filter(n => !n.seen).length;

    markAllSeen = () => {
        this.history = this.history.map(n => n.seen ? n : { ...n, seen: true });
        this.emitHistoryChange();
    }

    clearHistory = () => {
        this.history = [];
        this.emitHistoryChange();
    }

    addChangeListener = (callback) => {
        this.changeListeners.push(callback);
    }

    removeChangeListener = (callback) => {
        this.changeListeners = this.changeListeners.filter(cb => cb !== callback);
    }

    addHistoryListener = (callback) => {
        this.historyListeners.push(callback);
    }

    removeHistoryListener = (callback) => {
        this.historyListeners = this.historyListeners.filter(cb => cb !== callback);
    }

    emitChange = () => {
        // Emit per-item copies so listeners can't mutate the store's internal state
        this.changeListeners.forEach(cb => cb(this.visible.map(n => ({ ...n }))));
    }

    emitHistoryChange = () => {
        this.historyListeners.forEach(cb => cb(this.history.map(n => ({ ...n }))));
    }
}

export default new NotificationStore();
